import React from 'react';
import { getAttachmentDownloadUrl } from '../../api/vipMpApi';
import { CommunicationPanel } from '../../shared/components/CommunicationPanel';
import { useToast } from '../../context/ToastContext';
import { useVipMpSession } from '../../context/VipMpSessionContext';
import type { ReturnRequestDetail } from '../../types/vipMp';
import {
  detailHeader,
  formatDate,
  formatDateTime,
  formatMoney,
  partnerFacingStatus,
  pick,
  resolveRequestNumber,
  statusClassName
} from './partnerData';

type Props = {
  detail: ReturnRequestDetail;
  returnRequestId: number;
  onBack: () => void;
  onStartUpdate?: (ctx: { returnRequestId: number; requestNumber: string }) => void;
};

export function PartnerSubmissionDetail({ detail, returnRequestId, onBack, onStartUpdate }: Props) {
  const { showToast } = useToast();
  const { partnerContactName, partnerContactEmail } = useVipMpSession();
  const header = detailHeader(detail);
  const requestNumber = resolveRequestNumber(header) || `Request ${returnRequestId}`;
  const status = partnerFacingStatus(header);
  const currency =
    pick(header, ['CurrencyCode', 'currencyCode', 'RequestCurrencyCode', 'requestCurrencyCode']) ||
    pick((detail.lineItems[0] || {}) as Record<string, unknown>, [
      'CurrencyCode', 'currencyCode',
      'LineCurrencyCode', 'lineCurrencyCode',
      'RequestCurrencyCode', 'requestCurrencyCode'
    ]) ||
    'USD';

  const submittedBy = pick(header, ['FiledByActorName', 'filedByActorName', 'SubmitterName', 'submitterName', 'PartnerContactSubmitter', 'partnerContactSubmitter']);
  const submittedAt = pick(header, ['SubmittedUtc', 'submittedUtc', 'CreatedUtc', 'createdUtc']);
  const orderNumber = pick(header, ['AdobeSalesOrderNumber', 'adobeSalesOrderNumber']);
  const partnerComments = pick(header, ['PartnerComments', 'partnerComments', 'Comments', 'comments']);

  const needsReply =
    status.toLowerCase().includes('pending partner') ||
    status.toLowerCase().includes('needs reply') ||
    status.toLowerCase().includes('need') ||
    status.toLowerCase().includes('reply');

  const omAskMessage = (() => {
    const sorted = [...detail.statusHistory].sort((a, b) => {
      const t1 = a.ChangedUtc ? new Date(String(a.ChangedUtc)).getTime() : 0;
      const t2 = b.ChangedUtc ? new Date(String(b.ChangedUtc)).getTime() : 0;
      return t2 - t1;
    });
    for (const sh of sorted) {
      const code = String(sh.ToStatusCode ?? sh.toStatusCode ?? '').toUpperCase();
      const reason = String(sh.ChangeReason ?? sh.changeReason ?? '').trim();
      if (code === 'PENDING_PARTNER_ACTION' && reason) {
        return reason.replace(/^Information requested from partner:\s*/i, '').trim();
      }
    }
    return '';
  })();

  const downloadAttachment = async (attachment: Record<string, unknown>) => {
    const attachmentId = Number.parseInt(
      pick(attachment, ['ReturnRequestAttachmentId', 'returnRequestAttachmentId', 'AttachmentId', 'attachmentId']),
      10
    );
    if (Number.isNaN(attachmentId)) return;
    try {
      const { url } = await getAttachmentDownloadUrl(returnRequestId, attachmentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Download failed', 'error');
    }
  };

  return (
    <div id="partner-detail" className="tab-content active">
      <div className="breadcrumb">
        <a href="#back" onClick={(e) => { e.preventDefault(); onBack(); }}>
          My Submissions
        </a>
        <span>›</span>
        <span>{requestNumber}</span>
      </div>

      {/* Case header card */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="case-header">
          <div className="case-title">
            <h2>{requestNumber}</h2>
            <div className="submitted-details-boxes">
              <div className="submitted-detail-box">
                <span className="case-meta-label">Submitted date</span>
                <span className="case-meta-value">{formatDateTime(submittedAt)}</span>
              </div>
              <div className="submitted-detail-box">
                <span className="case-meta-label">Submitted by</span>
                <span className="case-meta-value">{submittedBy || '-'}</span>
              </div>
              <div className="submitted-detail-box">
                <span className="case-meta-label">Order #</span>
                <span className="case-meta-value">{orderNumber || '-'}</span>
              </div>
            </div>
          </div>
          <div className="case-actions">
            <span className={`status-badge ${statusClassName(status)}`} style={{ fontSize: 13 }}>
              {status}
            </span>
            {needsReply && onStartUpdate && (
              <button
                type="button"
                className="btn btn-primary"
                style={{ marginLeft: 12 }}
                onClick={() => onStartUpdate({ returnRequestId, requestNumber })}
              >
                Update Submission
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Action Required alert — shown only when Pending Partner Action */}
      {needsReply && (
        <div
          style={{
            background: '#fce4ec',
            border: '1px solid #f48fb1',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f5a623"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0, marginTop: 2 }}
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#c2185b' }}>Action Required</strong>
            <p style={{ color: '#880e4f', fontSize: 13, marginTop: 4, marginBottom: 0 }}>
              Additional information has been requested. Please click <strong>Update Submission</strong> above to respond and continue processing your request.
            </p>
            {omAskMessage && (
              <div
                style={{
                  marginTop: 10,
                  padding: '10px 12px',
                  background: 'rgba(194, 24, 91, 0.08)',
                  borderRadius: 6,
                  borderLeft: '3px solid #c2185b',
                  fontSize: 13,
                  color: '#880e4f',
                }}
              >
                <strong>Request message:</strong> {omAskMessage}
              </div>
            )}
          </div>
        </div>
      )}

      {/* View-only notice — shown when no action is needed */}
      {!needsReply && (
        <div
          style={{
            background: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
            fontSize: 13,
            color: '#555',
          }}
        >
          This request is view-only. You can only respond when status is{' '}
          <strong>Pending partner action</strong>.
        </div>
      )}

      {/* Request Summary */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <h2>Request Summary</h2>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div className="detail-section">
              <h3>Order Information</h3>
              <div className="detail-row">
                <span className="detail-label">Partner Name</span>
                <span className="detail-value">{pick(header, ['PartnerName', 'partnerName']) || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Contact Name</span>
                <span className="detail-value">
                  {pick(header, ['SubmitterName', 'submitterName', 'PartnerContactName', 'partnerContactName']) || '-'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Contact Email</span>
                <span className="detail-value">
                  {pick(header, ['PartnerContactEmail', 'partnerContactEmail', 'SubmitterEmail', 'submitterEmail']) || '-'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Order Number</span>
                <span className="detail-value">{orderNumber || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Replacement Order</span>
                <span className="detail-value">
                  {pick(header, ['ReplacementOrderNumber', 'replacementOrderNumber']) || '-'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Order Date</span>
                <span className="detail-value">{formatDate(pick(header, ['OrderDate', 'orderDate']))}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Customer</span>
                <span className="detail-value">{pick(header, ['CustomerName', 'customerName']) || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Marketplace Account ID</span>
                <span className="detail-value">
                  {pick(header, ['CustomerMarketplaceAccountId', 'customerMarketplaceAccountId']) || '-'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">GEO</span>
                <span className="detail-value">{pick(header, ['GeoCode', 'geoCode']) || '-'}</span>
              </div>
            </div>

            <div className="detail-section">
              <h3>Return Details</h3>
              <div className="detail-row">
                <span className="detail-label">Return Number</span>
                <span className="detail-value">{pick(header, ['ReturnNumber', 'returnNumber']) || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Return Type</span>
                <span className="detail-value">{pick(header, ['ReturnTypeCode', 'returnTypeCode']) || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Reason</span>
                <span className="detail-value">{pick(header, ['ReasonCode', 'reasonCode']) || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Time frame</span>
                <span className="detail-value">{pick(header, ['TimeframeCode', 'timeframeCode']) || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Currency</span>
                <span className="detail-value">{currency}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Return Sum</span>
                <span className="detail-value">
                  {formatMoney(pick(header, ['ReturnAmount', 'returnAmount', 'ReturnSum', 'returnSum']), currency)}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Fee</span>
                <span className="detail-value">{formatMoney(pick(header, ['FeeAmount', 'feeAmount']), currency)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Adobe Account Manager</span>
                <span className="detail-value">
                  {pick(header, ['AdobeAccountManagerName', 'adobeAccountManagerName', 'ManagerName', 'managerName']) || '-'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Adobe Account Manager Email</span>
                <span className="detail-value">
                  {pick(header, ['AdobeAccountManagerEmail', 'adobeAccountManagerEmail', 'ManagerEmail', 'managerEmail']) || '-'}
                </span>
              </div>
            </div>
          </div>

          {detail.lineItems.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 14, marginBottom: 8 }}>Line items</h3>
              <table className="data-table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Deploy To ID</th>
                    <th>Unit price</th>
                    <th>Qty</th>
                    <th>Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lineItems.map((line, index) => {
                    const row = line as Record<string, unknown>;
                    const qty = Number.parseFloat(pick(row, ['Quantity', 'quantity']) || '0');
                    const unitPrice = Number.parseFloat(
                      pick(row, ['UnitPrice', 'unitPrice', 'PartnerPrice', 'partnerPrice']) || '0'
                    );
                    const rowCurrency = pick(row, ['CurrencyCode', 'currencyCode', 'LineCurrencyCode', 'lineCurrencyCode', 'RequestCurrencyCode', 'requestCurrencyCode']) || currency;
                    return (
                      <tr key={String(index)}>
                        <td>{pick(row, ['ProductDescription', 'productDescription']) || '-'}</td>
                        <td>{pick(row, ['AdobeSku', 'adobeSku']) || '-'}</td>
                        <td>{pick(row, ['DeployToId', 'deployToId']) || '-'}</td>
                        <td>{formatMoney(unitPrice, rowCurrency)}</td>
                        <td>{pick(row, ['Quantity', 'quantity']) || '-'}</td>
                        <td>{formatMoney(unitPrice * qty, rowCurrency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Partner comments & attachments */}
      <div className="card">
        <div className="card-header">
          <h2>Partner comments &amp; attachments</h2>
        </div>
        <div className="card-body">
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, margin: '0 0 8px 0', color: '#333' }}>Comments from your request</h3>
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 10px 0' }}>
              Text saved from the submission form (additional / partner comments field). Status updates and request
              communications are sent to you by email.
            </p>
            <div
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                padding: '12px 14px',
                background: '#fafafa',
                fontSize: 13,
                color: '#333',
                whiteSpace: 'pre-wrap',
                minHeight: 48,
              }}
            >
              {partnerComments || '—'}
            </div>
          </div>

          <div>
            <h3 style={{ fontSize: 14, margin: '0 0 8px 0', color: '#333' }}>Attachments</h3>
            {detail.attachments.length === 0 && (
              <p style={{ color: '#666', fontSize: 13 }}>No attachments uploaded.</p>
            )}
            {detail.attachments.length > 0 && (
              <div style={{ display: 'grid', gap: 10 }}>
                {detail.attachments.map((attachment, index) => {
                  const row = attachment as Record<string, unknown>;
                  const fileName = pick(row, ['FileName', 'fileName']) || `Attachment ${index + 1}`;
                  return (
                    <div
                      key={String(index)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '10px 12px',
                        border: '1px solid #eee',
                        borderRadius: 8,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, color: '#333' }}>{fileName}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          Uploaded {formatDateTime(pick(row, ['CreatedUtc', 'createdUtc']))}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => void downloadAttachment(row)}
                      >
                        Download
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <CommunicationPanel
        returnRequestId={returnRequestId}
        currentPersona="partner"
        currentUser={{ name: partnerContactName, email: partnerContactEmail }}
        caseContacts={(() => {
          const seen = new Set<string>([partnerContactEmail?.toLowerCase()].filter(Boolean) as string[]);
          const contacts: { name: string; email: string; role: 'submitter' | 'partner_contact' }[] = [];
          const add = (n: string | null, e: string | null, role: 'submitter' | 'partner_contact') => {
            if (!n || !e) return;
            const key = e.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            contacts.push({ name: n, email: e, role });
          };
          add(
            pick(header, ['CurrentAssigneeName', 'currentAssigneeName']),
            pick(header, ['CurrentAssigneeEmail', 'currentAssigneeEmail']),
            'partner_contact'
          );
          add(
            pick(header, ['SubmitterName', 'submitterName']),
            pick(header, ['SubmitterEmail', 'submitterEmail']),
            'submitter'
          );
          return contacts;
        })()}
      />
    </div>
  );
}
